"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  Plus,
  MapPin,
  Phone,
  Home,
  Pencil,
  Trash2,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import AddressModal from "@/components/AddressModal";
import Modal from "@/components/Modal";
import Spinner from "@/components/Spinner";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import useAddress from "@/hooks/useAddress";

const AddressesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userDefaultAddress = useSelector(
    (state) => state.user.userInfo?.defaultAddress
  );

  // Use the centralized address hook
  const {
    addresses,
    isLoading,
    showModal,
    setShowModal,
    selectedAddress,
    setSelectedAddress,
    showDeleteModal,
    addressToDelete,
    settingDefaultId,
    isDeletingAddress,
    fetchAddresses,
    handleDeleteAddress,
    openDeleteModal,
    closeDeleteModal,
    handleSetDefaultAddress,
    resetModal,
    handleAddressComplete,
  } = useAddress();

  useEffect(() => {
    fetchAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="font-sans">
      <header className="bg-white shadow-md sticky top-0 z-10 section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-primary-100 transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 ml-4">
            {t("addresses.myAddresses")}
          </h1>
        </div>
      </header>
      <main className="p-4">
        {/* Info box for default address */}
        {addresses?.length > 0 && userDefaultAddress && (
          <div className="max-w-2xl mx-auto mb-6">
            <div className="flex items-center bg-blue-50 border border-blue-200 rounded-lg p-3 shadow-sm">
              <MapPin className="w-6 h-6 text-blue-500 mr-3 flex-shrink-0" />
              <span className="text-blue-800 text-sm font-medium">
                {t("addresses.defaultAddressInfoSimple")}
              </span>
            </div>
          </div>
        )}
        {isLoading && !addresses?.length && <Spinner />}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {!addresses?.length && !isLoading ? (
            <div className="bg-white rounded-lg shadow-md p-6 text-center section">
              <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">
                {t("addresses.noAddressesFound")}
              </h2>
              <p className="text-gray-600 mb-4">
                {t("addresses.addNewAddressToStart")}
              </p>
              <button
                onClick={() => setShowModal(true)}
                className="bg-primary-100  text-white px-6 py-3 rounded-full font-semibold hover:bg-primary-100  transition-colors duration-200"
              >
                {t("addresses.addNewAddress")}
              </button>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {addresses?.map((address) => {
                const isDeafult = userDefaultAddress?._id === address._id;
                return (
                  <motion.div
                    key={address._id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-lg shadow-lg p-6 border border-gray-200 hover:shadow-xl transition-shadow duration-300 section"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-xl font-semibold text-gray-900">
                        {address.name}
                      </h3>
                      {isDeafult && (
                        <span className="bg-primary-100 border-primary-100 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                          {t("addresses.default")}
                        </span>
                      )}
                    </div>
                    
                    {/* Warning if nearby location is deleted */}
                    {address.nearby?.isDeleted && (
                      <div className="mb-3 p-2 bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-400 dark:border-orange-600 rounded-md">
                        <p className="text-xs font-medium text-orange-800 dark:text-orange-200">
                          ⚠️ {t("addresses.locationUnavailable")}
                        </p>
                        <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">
                          {t("addresses.pleaseUpdateLocation")}
                        </p>
                      </div>
                    )}
                    
                    <div className="space-y-3 text-gray-600">
                      <p className="flex items-start">
                        <MapPin className="w-5 h-5 mr-2 text-primary-100border-primary-100 flex-shrink-0 mt-1" />
                        <span>
                          {address.address}, {address.city}, {address.state},{" "}
                          {address.country} - {address.pin_code}
                        </span>
                      </p>
                      <p className="flex items-center">
                        <Phone className="w-5 h-5 mr-2 text-primary-100border-primary-100 flex-shrink-0" />
                        <span>{address.mobile}</span>
                      </p>
                      {address.landmark && (
                        <p className="flex items-center">
                          <Home className="w-5 h-5 mr-2 text-primary-100border-primary-100 flex-shrink-0" />
                          <span>{address.landmark}</span>
                        </p>
                      )}
                    </div>
                    <div className="mt-6 flex justify-between items-center">
                      <div className="space-x-2">
                        <button
                          onClick={() => {
                            setSelectedAddress(address);
                            setShowModal(true);
                          }}
                          className="text-primary-100 transition-colors p-2 bg-primary-100/10 rounded-full"
                          aria-label="Edit address"
                          disabled={!!settingDefaultId}
                        >
                          <Pencil className="w-5 h-5" />
                        </button>
                        {!isDeafult && (
                          <button
                            onClick={() => openDeleteModal(address._id)}
                            className="text-red-600 hover:text-red-800 transition-colors p-2 bg-red-100 rounded-full"
                            aria-label="Delete address"
                            disabled={!!settingDefaultId}
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                      {!isDeafult && (
                        <button
                          onClick={() => handleSetDefaultAddress(address._id)}
                          disabled={!!settingDefaultId}
                          className={`text-primary-100 transition-colors text-sm font-medium px-3 py-1 rounded-md flex items-center gap-2 ${
                            settingDefaultId === address._id
                              ? "bg-primary-100/20"
                              : "bg-primary-100/10 hover:bg-primary-100/20"
                          }`}
                        >
                          {settingDefaultId === address._id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-100"></div>
                          ) : (
                            t("addresses.setAsDefault")
                          )}
                        </button>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>
      </main>

      {addresses?.length > 0 && (
        <button
          onClick={() => {
            resetModal();
            setShowModal(true);
          }}
          className="fixed bottom-[70px] right-6 bg-primary-100  text-white p-3 rounded-full shadow-lg hover:bg-primary-100  transition-colors duration-200"
          aria-label="Add new address"
        >
          <Plus className="w-5 h-5" />
        </button>
      )}

      <AddressModal
        isOpen={showModal}
        onClose={resetModal}
        initialValues={selectedAddress || {}}
        onComplete={handleAddressComplete}
        isEdit={!!selectedAddress}
      />
      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={closeDeleteModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 20 }}
              className="bg-white section rounded-lg shadow-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <Trash2 className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">
                  {t("addresses.deleteAddress")}
                </h3>
                <p className="text-gray-600 mb-6">
                  {t("addresses.deleteConfirmation")}
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={closeDeleteModal}
                    disabled={isDeletingAddress}
                    className="flex-1 px-4 py-2 border-2 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 rounded-md font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    onClick={() => handleDeleteAddress(addressToDelete)}
                    disabled={isDeletingAddress}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isDeletingAddress && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    )}
                    {t("common.delete")}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AddressesPage;
